import type { LinkFormProps } from '@/components/admin/liens/link-form';
import { ReferenceField } from '@/components/admin/reglages/reference-list';
import { RequiredMark } from '@/components/admin/required-mark';
import { VitrineLinkEntryRow } from '@/components/admin/vitrine/vitrine-link-entry-row';
import type { LinkFormDraft } from '@/lib/link-form';
import {
  addLinkRow,
  canSubmitLink,
  editLinkRow,
  linkExperienceLookupOf,
  linkFormProblems,
  removeLinkRow,
} from '@/lib/link-form';
import { LINK_LIFETIME_LABEL } from '@/lib/link-labels';
import type { LinkEntryProblem, LinkLifetime } from '@matter_extractor/shared';
import { LINK_ENTRIES_MAX, LINK_LIFETIMES, LINK_NAME_MAX } from '@matter_extractor/shared';
import { Button, Select } from '@pms-digital/design-system';
import { useState } from 'react';

/**
 * The form of a link on the VITRINE (docs/specs/vitrine.md sections 3.4 and 7,
 * V5, maquette `vitrine.html` section 4, issue #192) - the creation and
 * « Modifier » alike, as `link-form.tsx` is on the atelier.
 *
 * What a link of the vitrine does not have is a choice of version: every entry
 * plays **the latest version in production** of its experience, so the select of
 * the version, and with it the tag, are gone, and the form says so in one
 * sentence under the entries. Only the experiences that HAVE a version in
 * production are offered. The name, the lifetime, the refusals and the button are
 * the atelier's, through the same pure helpers of `lib/link-form.ts` - and the
 * same `LinkFormProps`, so the two call sites swap one form for the other
 * (`role-link-form.tsx`) and the page is not told which it holds.
 *
 * It never touches a row's version: the rows come from `lib/link-form.ts`, which
 * gives a new row the default of every selector - the production pointer, that
 * is the latest version in production on a vitrine.
 */

export function VitrineLinkForm({
  initial,
  experiences,
  experiencesRead,
  submitLabel,
  submitting,
  serverProblems,
  failure,
  testId,
  onSubmit,
  onCancel,
}: LinkFormProps) {
  const [draft, setDraft] = useState<LinkFormDraft>(initial);
  // An entry of the vitrine is always `latest` on the `prod` side (vitrine.md §3.4, V5):
  // what the form judges and sends says so, whatever the rows were born with.
  const onProd: LinkFormDraft = {
    ...draft,
    entries: draft.entries.map((row) => ({ ...row, pin: 'latest', side: 'prod' })),
  };

  const localProblems = experiencesRead
    ? linkFormProblems(onProd, linkExperienceLookupOf(experiences))
    : [];
  const problems = mergeProblems(localProblems, serverProblems);
  const armed = canSubmitLink(draft, problems) && !submitting;

  /*
    The experiences a row may choose from: those with a version in production,
    plus the one the row already names (a link being modified may name one that
    has since been retired).
  */
  const inProduction = experiences.filter(
    (card) => card.production !== null && card.retiredAt === null,
  );
  const offeredTo = (experienceId: string) =>
    experienceId === '' || inProduction.some((card) => card.id === experienceId)
      ? inProduction
      : [...inProduction, ...experiences.filter((card) => card.id === experienceId)];

  return (
    <form
      data-testid={testId}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (armed) onSubmit(onProd);
      }}
      className="space-y-5"
    >
      <div className="max-w-xl">
        <ReferenceField
          label="Nom"
          testId="partage-nom"
          value={draft.name}
          max={LINK_NAME_MAX}
          placeholder="Atelier du 24 septembre"
          onChange={(name) => setDraft((current) => ({ ...current, name }))}
        />
        <p className="pt-1 text-caption text-gray-500">
          Pour vous : la personne ne le voit jamais.
        </p>
      </div>

      {/* The package `Select` (Radix): its mark is on this wrapper, and the value arrives
          as a string - no event, so no `currentTarget` to read before the updater. */}
      <div data-testid="partage-duree" className="max-w-xs">
        <Select
          label="Durée de vie"
          required
          value={draft.lifetime}
          disabled={submitting}
          options={LINK_LIFETIMES.map((lifetime) => ({
            value: lifetime,
            label: LINK_LIFETIME_LABEL[lifetime],
          }))}
          onValueChange={(value) => {
            const lifetime = value as LinkLifetime;
            setDraft((current) => ({ ...current, lifetime }));
          }}
          help="À partir de la création. Prolongeable ensuite depuis la liste."
        />
      </div>

      <fieldset className="space-y-3">
        <legend className="font-semibold text-caption text-gray-800">
          Entrées
          <RequiredMark />
        </legend>
        <ul className="space-y-4">
          {draft.entries.map((row, index) => (
            <VitrineLinkEntryRow
              key={row.key}
              row={row}
              index={index}
              offered={offeredTo(row.experienceId)}
              problem={problems.find((problem) => problem.index === index) ?? null}
              removable={draft.entries.length > 1}
              disabled={submitting}
              onChange={(patch) => setDraft((current) => editLinkRow(current, index, patch))}
              onRemove={() => setDraft((current) => removeLinkRow(current, index))}
            />
          ))}
        </ul>
        <p data-testid="partage-vitrine-derniere" className="text-caption text-gray-700">
          Joue toujours la dernière version en prod de l’expérience. Seules les expériences qui ont
          une version sur la prod sont proposées.
        </p>
        <Button
          type="button"
          variant="accent"
          size="sm"
          data-testid="partage-ajouter-entree"
          disabled={submitting || draft.entries.length >= LINK_ENTRIES_MAX}
          onClick={() => setDraft((current) => addLinkRow(current))}
          icon="add"
        >
          Ajouter une entrée
        </Button>
        <p className="text-caption text-gray-500">
          Une seule entrée : le lien démarre directement. Plusieurs : la page de choix montre le nom
          et la courte description de chacune.
        </p>
      </fieldset>

      {/* Mounted from the first render, empty or not: a live region created at
          the same moment it fills is not read out. */}
      <p aria-live="polite" className="text-rouge-deep text-small">
        {failure}
      </p>

      <div className="flex items-center gap-3">
        <Button variant="secondary" type="submit" data-testid="partage-soumettre" disabled={!armed}>
          {submitLabel}
        </Button>
        {onCancel === undefined ? null : (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
            Annuler
          </Button>
        )}
      </div>
    </form>
  );
}

/** One list of refusals out of two, the SERVER's winning on a row both name (see `link-form.tsx`). */
function mergeProblems(
  local: readonly LinkEntryProblem[],
  server: readonly LinkEntryProblem[],
): LinkEntryProblem[] {
  const byIndex = new Map<number, LinkEntryProblem>();
  for (const problem of local) byIndex.set(problem.index, problem);
  for (const problem of server) byIndex.set(problem.index, problem);
  return [...byIndex.values()].sort((a, b) => a.index - b.index);
}
